import i18n from "i18next";
import {
	IconAlertTriangle,
	IconBook,
	IconClock,
	IconLink,
	IconListNumbers,
	IconMath,
	IconMessageCircle,
	IconShieldCheck,
	IconX,
} from "@tabler/icons-react";
import { type ReactNode, useEffect } from "react";
import { cn } from "@/lib/cn";

// ds24 Arrangement A (handoff 25). A single 52px LEFT utility rail with two
// clusters — structure on top (Outline / Bibliography / Cross-references /
// Symbols), review pinned
// to the bottom (Comments / Validate / History). One 258px panel open at a
// time (VS-Code toggle: clicking the active icon collapses it). Source + Ask
// Lilia stay on the RIGHT — this rail is left-only by design.
export type PanelId =
	| "outline"
	| "refs"
	| "xrefs"
	| "syms"
	| "review"
	| "validate"
	| "history";

/**
 * Each slot's shortcut, assigned per slot — not derived from its position.
 * Olivia, 25 Sep: moving a slot must never renumber the others. ⌘7 is
 * Cross-references because it arrived last, and it keeps that number wherever
 * it sits in the rail.
 */
export const PANEL_SHORTCUT: Record<PanelId, number> = {
	outline: 1,
	refs: 2,
	syms: 3,
	review: 4,
	validate: 5,
	history: 6,
	xrefs: 7,
};

export const shortcutLabel = (id: PanelId) => `⌘${PANEL_SHORTCUT[id]}`;

/** The slot a ⌘-digit opens, if any. */
export function panelForShortcut(digit: number): PanelId | undefined {
	return (Object.keys(PANEL_SHORTCUT) as PanelId[]).find((id) => PANEL_SHORTCUT[id] === digit);
}

interface RailItem {
	id: PanelId;
	label: string;
	icon: ReactNode;
	/** Show a --warning dot on the icon (unresolved comments / validation). */
	warn?: boolean;
}

const TOP_CLUSTER: Omit<RailItem, "warn">[] = [
	{ id: "outline", label: i18n.t("editor:panelRail.outline"), icon: <IconListNumbers size={20} /> },
	// Neither panel is called "References" (Olivia, 25 Sep): to someone reading
	// a paper the word means the bibliography, to someone writing LaTeX it means
	// \ref. Whichever panel owned it, the author would open the wrong one half
	// the time. Bibliography and Cross-references are each LaTeX's own word.
	{ id: "refs", label: i18n.t("editor:panelRail.bibliography"), icon: <IconBook size={20} /> },
	{ id: "xrefs", label: i18n.t("editor:panelRail.crossReferences"), icon: <IconLink size={20} /> },
	{ id: "syms", label: i18n.t("editor:panelRail.symbols"), icon: <IconMath size={20} /> },
];
const BOTTOM_CLUSTER: Omit<RailItem, "warn">[] = [
	{ id: "review", label: i18n.t("editor:panelRail.comments"), icon: <IconMessageCircle size={20} /> },
	{ id: "validate", label: i18n.t("editor:panelRail.validate"), icon: <IconShieldCheck size={20} /> },
	{ id: "history", label: i18n.t("editor:panelRail.history"), icon: <IconClock size={20} /> },
];

const TITLES: Record<PanelId, string> = {
	outline: i18n.t("editor:panelRail.outline"),
	refs: i18n.t("editor:panelRail.bibliography"),
	xrefs: i18n.t("editor:panelRail.crossReferences"),
	syms: i18n.t("editor:panelRail.symbols"),
	review: i18n.t("editor:panelRail.comments"),
	validate: i18n.t("editor:panelRail.validate"),
	history: i18n.t("editor:panelRail.history"),
};

interface PanelRailProps {
	/** Open panel; null = collapsed (rail-only). */
	active: PanelId | null;
	onChange: (panel: PanelId | null) => void;
	width?: number;
	/** Optional right-aligned count in the panel header (e.g. "16 blocks"). */
	count?: (panel: PanelId) => string | undefined;
	/** Unresolved markers → --warning dot on the rail icon. */
	commentsWarn?: boolean;
	validateWarn?: boolean;
	outline: ReactNode;
	refs: ReactNode;
	xrefs: ReactNode;
	syms: ReactNode;
	review: ReactNode;
	validate: ReactNode;
	history: ReactNode;
	/** Extra action icons pinned below the rail (e.g. Block-mode Docs +
	 *  Ask Lilia, which aren't panel-host panels). Rendered after a divider. */
	extras?: { id: string; label: string; icon: ReactNode; active?: boolean; onClick: () => void; testid?: string }[];
}

export function PanelRail({
	active,
	onChange,
	width = 258,
	count,
	commentsWarn,
	validateWarn,
	outline,
	refs,
	xrefs,
	syms,
	review,
	validate,
	history,
	extras,
}: PanelRailProps) {
	// Esc collapses the open panel; ⌘-digit toggles the slot that owns it.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape" && active) {
				onChange(null);
				return;
			}
			if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey) {
				const id = panelForShortcut(Number(e.key));
				if (id) {
					e.preventDefault();
					onChange(active === id ? null : id);
				}
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
	}, [active, onChange]);

	const body: Record<PanelId, ReactNode> = { outline, refs, xrefs, syms, review, validate, history };

	return (
		<aside
			className="shrink-0 self-stretch flex"
			data-testid="panel-rail"
			aria-label={i18n.t("editor:panelRail.documentPanels")}
		>
			{/* 52px icon rail — subtly tinted so it reads as nav, not page. */}
			<div
				className="flex flex-col items-center py-2 gap-1 shrink-0 border-r border-[var(--lilia-border)]"
				style={{
					width: 52,
					// Derived from the chat ground rather than the page ground, so the rail
					// moves with the panel beside it instead of staying blue against a white
					// panel — the seam would otherwise land in the wrong place.
					background:
						"color-mix(in srgb, var(--lilia-bg-muted) 55%, var(--lilia-panel-ground))",
				}}
				role="tablist"
				aria-orientation="vertical"
			>
				{TOP_CLUSTER.map((it) => (
					<RailIcon key={it.id} item={it} active={active === it.id} onClick={() => onChange(active === it.id ? null : it.id)} />
				))}
				<div className="flex-1" />
				<div className="w-6 h-px bg-[var(--lilia-border)] my-1.5" />
				{BOTTOM_CLUSTER.map((it) => (
					<RailIcon
						key={it.id}
						item={{ ...it, warn: it.id === "review" ? commentsWarn : it.id === "validate" ? validateWarn : false }}
						active={active === it.id}
						onClick={() => onChange(active === it.id ? null : it.id)}
					/>
				))}
				{extras && extras.length > 0 && (
					<>
						<div className="w-6 h-px bg-[var(--lilia-border)] my-1.5" />
						{extras.map((ex) => (
							<button
								key={ex.id}
								type="button"
								data-testid={ex.testid ?? `panel-rail-extra-${ex.id}`}
								onClick={ex.onClick}
								title={ex.label}
								aria-label={ex.label}
								aria-pressed={!!ex.active}
								className={cn(
									"relative flex items-center justify-center border-none rounded-lg cursor-pointer transition-colors duration-150 w-9 h-9 bg-transparent",
									"text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)] hover:bg-[color-mix(in_srgb,var(--lilia-text)_8%,transparent)]",
									ex.active && "text-[var(--lilia-primary)] bg-[var(--lilia-primary-subtle)]",
								)}
							>
								{ex.icon}
							</button>
						))}
					</>
				)}
			</div>

			{/* 258px panel host — to the RIGHT of the rail. */}
			{active && (
				<div
					data-testid="panel-active"
					data-panel={active}
					className="flex flex-col bg-[var(--lilia-panel-ground)] border-r border-[var(--lilia-border)] shrink-0"
					style={{ width }}
				>
					<div className="flex items-center gap-2 px-3 pl-3.5 h-11 border-b border-[var(--lilia-border)] shrink-0">
						<span className="text-[13px] font-semibold text-[var(--lilia-text)]">{TITLES[active]}</span>
						{count?.(active) && <span className="text-[11px] text-[var(--lilia-text-muted)]">{count(active)}</span>}
						<button
							data-testid="panel-close"
							onClick={() => onChange(null)}
							title={i18n.t("editor:panelRail.collapsePanelEsc")}
							aria-label={i18n.t("editor:panelRail.collapsePanel")}
							className="ml-auto w-6.5 h-6.5 flex items-center justify-center rounded-md text-[var(--lilia-text-muted)] hover:bg-[var(--lilia-hover)] hover:text-[var(--lilia-text)]"
						>
							<IconX size={15} />
						</button>
					</div>
					<div className="flex-1 min-h-0 overflow-y-auto">{body[active]}</div>
				</div>
			)}
		</aside>
	);
}

function RailIcon({ item, active, onClick }: { item: RailItem; active: boolean; onClick: () => void }) {
	return (
		<button
			type="button"
			data-testid="panel-rail-item"
			data-panel={item.id}
			role="tab"
			aria-selected={active}
			aria-pressed={active}
			onClick={onClick}
			title={`${item.label} (${shortcutLabel(item.id)})`}
			aria-label={item.label}
			className={cn(
				"relative flex items-center justify-center border-none rounded-lg cursor-pointer transition-colors duration-150 w-9 h-9 bg-transparent",
				"text-[var(--lilia-text-muted)] hover:text-[var(--lilia-text)] hover:bg-[color-mix(in_srgb,var(--lilia-text)_8%,transparent)]",
				active && [
					"text-[var(--lilia-primary)] bg-[var(--lilia-primary-subtle)]",
					// ds24 active marker: 3px primary spine on the rail's inner edge.
					"before:content-[''] before:absolute before:-left-[7px] before:top-[7px] before:bottom-[7px] before:w-[3px] before:rounded-r before:bg-[var(--lilia-primary)]",
				],
			)}
		>
			{item.icon}
			{item.warn && (
				<span
					data-testid="panel-rail-warn"
					className="absolute top-1.5 right-1.5 w-[7px] h-[7px] rounded-full bg-[var(--lilia-warning)]"
					style={{ border: "1.5px solid color-mix(in srgb, var(--lilia-bg-muted) 55%, var(--lilia-bg))" }}
				/>
			)}
		</button>
	);
}
